import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const TransitionToTeam = () => {
  const teamHighlights = [
    {
      role: 'Enterprise Engineering',
      description: 'We bring the rigorous testing, security, and architectural standards of big tech to your startup.',
      icon: 'Shield',
      color: 'from-blue-600 to-indigo-600'
    },
    {
      role: 'Product Sovereignty',
      description: 'Senior architects who own the lifecycle, from cloud infrastructure to pixel-perfect interfaces.',
      icon: 'Cpu',
      color: 'from-indigo-600 to-purple-600'
    },
    {
      role: 'Strategic Scaling',
      description: 'Expertise in building for the future, ensuring your MVP code is a foundation, not a liability.',
      icon: 'TrendingUp',
      color: 'from-purple-600 to-pink-600'
    }
  ];

  const stats = [
    { value: '10+', label: 'Enterprise Platforms', icon: 'Globe' },
    { value: 'Zero', label: 'Technical Debt', icon: 'Zap' },
    { value: '99.9%', label: 'Systems Uptime', icon: 'Server' },
    { value: 'Elite', label: 'Engineering DNA', icon: 'Award' }
  ];

  return (
    <section className="py-24 bg-slate-900 overflow-hidden relative">
      {/* Decorative Orbs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-[100px] -mr-48 -mt-48"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/5 rounded-full blur-[100px] -ml-48 -mb-48"></div>

      <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center space-x-2 bg-blue-500/10 border border-blue-500/20 text-blue-400 px-4 py-2 rounded-full text-sm font-bold uppercase tracking-widest mb-6">
            <Icon name="Users" size={14} />
            <span>The Enterprise Edge</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-8">
            From Fortune 500 to{' '}
            <span className="text-gradient bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Your Foundation
            </span>
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
            We spent a decade building the systems that power modern enterprises. Today, we
            deploy that elite engineering DNA to help high-growth startups move fast without breaking.
          </p>
        </motion.div>

        {/* Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {teamHighlights.map((highlight, index) => (
            <motion.div
              key={highlight.role}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white/5 border border-white/10 rounded-3xl p-10 hover:bg-white/10 transition-all group"
            >
              <div className={`w-14 h-14 bg-gradient-to-br ${highlight.color} rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-black/20`}>
                <Icon name={highlight.icon} size={24} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">{highlight.role}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{highlight.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-24 bg-white/5 border border-white/10 rounded-[3rem] p-12 backdrop-blur-md"
        >
          {stats.map((stat, index) => (
            <div key={stat.label} className="text-center">
              <div className="text-3xl md:text-4xl font-black text-white mb-2">{stat.value}</div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-widest">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Professional Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center"
        >
          <div className="bg-gradient-to-br from-blue-600 to-purple-700 rounded-[3rem] p-12 md:p-16 shadow-2xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32 group-hover:bg-white/20 transition-all"></div>

            <div className="relative z-10">
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-8">
                Ready to Deploy Enterprise Intelligence?
              </h3>
              <p className="text-white/80 text-lg mb-12 max-w-2xl mx-auto leading-relaxed">
                Due to the sensitive nature of our corporate portfolio (NDAs), we offer
                private deep-dive consultations to showcase relevant architectural patterns.
              </p>

              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <Link
                  to="/contact-engagement-hub"
                  className="bg-white text-slate-900 px-10 py-5 rounded-2xl font-black uppercase tracking-wider hover:scale-105 transition-all shadow-xl"
                >
                  Request Technical Audit
                </Link>

                <div className="flex items-center space-x-4 text-white/60">
                  <div className="w-10 h-10 border border-white/20 rounded-full flex items-center justify-center">
                    <Icon name="ShieldCheck" size={18} />
                  </div>
                  <span className="text-sm font-bold uppercase tracking-widest">Secure & Confidential</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TransitionToTeam;
